import type { AIIntakeReport, AIProviderReport } from "../../lib/ai-intake";

function providerName(provider: AIProviderReport["provider"]): string {
  if (provider === "anthropic") return "Anthropic";
  if (provider === "google") return "Google Gemini";
  return "OpenAI";
}

function detectedSummary(provider: AIProviderReport): string {
  if (provider.selectedModel) return `${providerName(provider.provider)} model: ${provider.selectedModel}`;
  if (provider.models.length > 1) return `${providerName(provider.provider)}: ${provider.models.length} models found`;
  return "";
}

function readablePaths(paths: readonly string[]): string {
  if (paths.length === 0) return "a private file";
  if (paths.length === 1) return paths[0]!;
  return `${paths.slice(0, -1).join(", ")} and ${paths[paths.length - 1]}`;
}

function providerKeyLabel(names: string[]): string {
  return `${names.join(" and ")} API ${names.length === 1 ? "key" : "keys"}`;
}

export function AISetupCard({ report, omittedPaths }: { report: AIIntakeReport; omittedPaths: string[] }) {
  if (report.status === "none") return null;
  const providerNames = report.providers.map(({ provider }) => providerName(provider));
  const providerDetails = report.providers.map(detectedSummary).filter(Boolean);
  const envKeyStaysLocal = report.providers.some(({ keyLocations }) => keyLocations.includes("omitted-env"));
  const privateKeyPaths = omittedPaths.filter((path) => /(?:^|\/)\.env(?:\.[^/]+)?$/i.test(path));

  if (report.status === "blocked") {
    const hasSecret = report.reasonCode === "SECRET_DETECTED";
    return (
      <aside className="ai-setup-card ai-setup-blocked" role="alert">
        <div className="ai-setup-icon" aria-hidden="true">↗</div>
        <div>
          <strong>{hasSecret ? "Stopped: an API key could be published" : "Update this app to use Doorway AI"}</strong>
          <p>We found the issue in <code>{readablePaths(report.blockedPaths)}</code>. Nothing has been uploaded or changed.</p>
          <p>{hasSecret
            ? "Remove and rotate the key, then call /.doorway/ai/v1/generate from the app."
            : "Call /.doorway/ai/v1/generate instead of a provider URL, SDK, or old managed route, then choose the folder again."}</p>
        </div>
      </aside>
    );
  }

  const title = envKeyStaysLocal
      ? `Kept private: ${providerKeyLabel(providerNames)}`
      : report.fixedRoute
        ? "Doorway AI route found"
        : `${providerNames.join(" and ")} AI connection found`;

  return (
    <aside className={`ai-setup-card${envKeyStaysLocal ? " ai-setup-private" : ""}`} aria-label="AI and API key check">
      <div className="ai-setup-icon" aria-hidden="true">{envKeyStaysLocal ? "⌂" : "✦"}</div>
      <div>
        <strong>{title}</strong>
        {providerDetails.length > 0 && <p className="ai-detected-models">{providerDetails.join(" · ")}</p>}
        {envKeyStaysLocal && (
          <p>We found it in <code>{readablePaths(privateKeyPaths)}</code>. Doorway checked this file in your browser, then left the whole file out.</p>
        )}
        {envKeyStaysLocal && (
          <p className="ai-privacy-note">This key stays on your computer. Connect the provider from Doorway after publishing.</p>
        )}
        {!envKeyStaysLocal && (
          <p>Your files stay unchanged. Choose one provider connection, model, and monthly AI-run limit after publishing.</p>
        )}
      </div>
    </aside>
  );
}
